import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { PasswordInput } from '../components/ui/password-input';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Loader2, Gift, Check, X, CreditCard, Shield } from 'lucide-react';
import { billingAPI } from '../lib/api';
import { LogoPublic } from '../components/Logo';
import { COUNTRIES, INDUSTRIES } from '../lib/i18n-config';
import { cn } from '../lib/utils';

export const Signup = () => {
  const [searchParams] = useSearchParams();
  const [companyName, setCompanyName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [businessCountry, setBusinessCountry] = useState('US');
  const [industry, setIndustry] = useState('');
  const [industryOtherText, setIndustryOtherText] = useState('');
  const [referralCode, setReferralCode] = useState(searchParams.get('ref') || '');
  const [referralValid, setReferralValid] = useState(null);
  const [referralChecking, setReferralChecking] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { signup } = useAuth();
  const navigate = useNavigate();

  // Trial config (feature flag)
  const [trialConfig, setTrialConfig] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState('');

  useEffect(() => {
    billingAPI.getTrialConfig()
      .then(r => {
        setTrialConfig(r.data);
        // Default to the middle plan if card required
        if (r.data.requires_card && r.data.plans?.length > 1) {
          setSelectedPlan(r.data.plans[1]?.tier_id || r.data.plans[0]?.tier_id || '');
        } else if (r.data.requires_card && r.data.plans?.length === 1) {
          setSelectedPlan(r.data.plans[0]?.tier_id || '');
        }
      })
      .catch(() => setTrialConfig({ requires_card: false, trial_days: 14, plans: [] }));
  }, []);

  const requiresCard = trialConfig?.requires_card;
  const trialDays = trialConfig?.trial_days || 14;

  const validateReferralCode = async (code) => {
    if (!code || code.length < 6) { setReferralValid(null); return; }
    setReferralChecking(true);
    try {
      const response = await billingAPI.validateReferralCode(code);
      setReferralValid(response.data.valid);
    } catch { setReferralValid(false); }
    finally { setReferralChecking(false); }
  };

  const handleReferralChange = (e) => {
    const code = e.target.value.toUpperCase();
    setReferralCode(code);
    if (code.length >= 8) { validateReferralCode(code); } else { setReferralValid(null); }
  };

  const handleIndustryChange = (val) => {
    setIndustry(val);
    if (val !== 'other') setIndustryOtherText('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await signup({
        company_name: companyName,
        first_name: firstName,
        last_name: lastName,
        email,
        password,
        referral_code: referralCode || null,
        accept_terms: acceptTerms,
        business_country: businessCountry || null,
        industry: industry || null,
        industry_other_text: industry === 'other' ? industryOtherText : null,
        plan_tier: requiresCard ? selectedPlan : null,
      });

      if (result.card_setup_required) {
        // Redirect to trial setup page (card collection via Stripe)
        navigate('/trial-setup');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  // Format price for display
  const fmtPrice = (cents) => `$${(cents / 100).toFixed(0)}`;

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-4">
          <LogoPublic size="lg" />
          <p className="text-muted-foreground mt-1">Fleet inventory management</p>
        </div>

        <Card className="border-border shadow-sm">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-bold font-heading">Create account</CardTitle>
            <CardDescription>
              {requiresCard
                ? `Start your ${trialDays}-day free trial — no charge today`
                : 'Set up your company and start tracking inventory'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-md" data-testid="signup-error">
                  {error}
                </div>
              )}

              {/* Plan Selection — only when card required */}
              {requiresCard && trialConfig?.plans?.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-sm font-bold uppercase tracking-wide">
                    Choose Your Inventory + CRM Plan
                  </Label>
                  <div className="grid gap-2" data-testid="plan-selector">
                    {trialConfig.plans.map((plan) => {
                      const userCount = plan.tier_id.includes('starter') ? 3
                        : plan.tier_id.includes('growth') ? 7 : 12;
                      return (
                        <button
                          key={plan.tier_id}
                          type="button"
                          onClick={() => setSelectedPlan(plan.tier_id)}
                          className={cn(
                            "w-full text-left p-3 rounded-lg border-2 transition-all",
                            selectedPlan === plan.tier_id
                              ? "border-primary bg-primary/5"
                              : "border-border hover:border-muted-foreground/30"
                          )}
                          data-testid={`plan-${plan.tier_id}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-sm">{plan.name.replace(/^CRM\s+/i, '')}</span>
                              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground font-medium">
                                {userCount} users
                              </span>
                            </div>
                            <span className="text-sm font-medium">
                              {fmtPrice(plan.price_monthly)}/mo
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-[11px] text-muted-foreground leading-relaxed">
                    Your card will not be charged today. Billing begins after your {trialDays}-day free trial.
                    Cancel anytime before the trial ends.
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="companyName" className="text-sm font-bold uppercase tracking-wide">
                  Company Name
                </Label>
                <Input
                  id="companyName"
                  type="text"
                  placeholder="ABC Plumbing"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  required
                  className="h-12 text-lg"
                  data-testid="signup-company"
                />
              </div>

              {/* First Name / Last Name */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="firstName" className="text-sm font-bold uppercase tracking-wide">
                    First Name
                  </Label>
                  <Input
                    id="firstName"
                    type="text"
                    placeholder="John"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                    className="h-12 text-lg"
                    data-testid="signup-first-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName" className="text-sm font-bold uppercase tracking-wide">
                    Last Name
                  </Label>
                  <Input
                    id="lastName"
                    type="text"
                    placeholder="Smith"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="h-12 text-lg"
                    data-testid="signup-last-name"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-bold uppercase tracking-wide">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-12 text-lg"
                  data-testid="signup-email"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-bold uppercase tracking-wide">
                  Password
                </Label>
                <PasswordInput
                  id="password"
                  placeholder="Min 6 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  className="h-12 text-lg"
                  data-testid="signup-password"
                />
              </div>

              {/* Business Country */}
              <div className="space-y-2">
                <Label className="text-sm font-bold uppercase tracking-wide">
                  Business Country
                </Label>
                <Select value={businessCountry} onValueChange={setBusinessCountry} required>
                  <SelectTrigger className="h-12 text-lg" data-testid="signup-business-country">
                    <SelectValue placeholder="Select your country..." />
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRIES.map(c => (
                      <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Industry */}
              <div className="space-y-2">
                <Label className="text-sm font-bold uppercase tracking-wide">
                  Industry
                </Label>
                <Select value={industry} onValueChange={handleIndustryChange} required>
                  <SelectTrigger className="h-12 text-lg" data-testid="signup-industry">
                    <SelectValue placeholder="Select your industry..." />
                  </SelectTrigger>
                  <SelectContent>
                    {INDUSTRIES.map(i => (
                      <SelectItem key={i.code} value={i.code}>{i.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Industry Other */}
              {industry === 'other' && (
                <div className="space-y-2">
                  <Label htmlFor="industryOther" className="text-sm font-bold uppercase tracking-wide">
                    Please specify your industry
                  </Label>
                  <Input
                    id="industryOther"
                    type="text"
                    placeholder="e.g. Marine Services"
                    value={industryOtherText}
                    onChange={(e) => setIndustryOtherText(e.target.value)}
                    required
                    className="h-12 text-lg"
                    data-testid="signup-industry-other"
                  />
                </div>
              )}

              {/* Referral Code */}
              <div className="space-y-2">
                <Label htmlFor="referralCode" className="text-sm font-bold uppercase tracking-wide flex items-center gap-2">
                  <Gift className="w-4 h-4 text-primary" />
                  Referral Code (optional)
                </Label>
                <div className="relative">
                  <Input
                    id="referralCode"
                    type="text"
                    placeholder="SL-XXXXXX"
                    value={referralCode}
                    onChange={handleReferralChange}
                    className="h-12 text-lg font-mono pr-10"
                    data-testid="signup-referral"
                  />
                  {referralChecking && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 animate-spin text-muted-foreground" />
                  )}
                  {!referralChecking && referralValid === true && (
                    <Check className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-green-500" />
                  )}
                  {!referralChecking && referralValid === false && (
                    <X className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-red-500" />
                  )}
                </div>
                {referralValid === false && referralCode.length >= 8 && (
                  <p className="text-xs text-destructive">Invalid referral code</p>
                )}
              </div>

              {/* Legal acceptance */}
              <div className="flex items-start gap-3 pt-1">
                <Checkbox
                  id="accept-terms"
                  checked={acceptTerms}
                  onCheckedChange={(checked) => setAcceptTerms(!!checked)}
                  data-testid="signup-accept-terms"
                />
                <label htmlFor="accept-terms" className="text-sm leading-snug cursor-pointer select-none">
                  I agree to the{' '}
                  <a href="https://stockandload.com/terms-of-service/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-medium">
                    Terms of Service
                  </a>{' '}
                  and{' '}
                  <a href="https://stockandload.com/privacy-policy/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-medium">
                    Privacy Policy
                  </a>
                </label>
              </div>

              <Button
                type="submit"
                className="w-full h-12 text-base font-bold btn-press"
                disabled={loading || !acceptTerms || !businessCountry || !industry || (requiresCard && !selectedPlan)}
                data-testid="signup-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating account...
                  </>
                ) : requiresCard ? (
                  <>
                    <CreditCard className="w-4 h-4 mr-2" />
                    Start Free Trial
                  </>
                ) : (
                  'Create account'
                )}
              </Button>

              {/* Trust badges for card-required flow */}
              {requiresCard && (
                <div className="flex items-center justify-center gap-4 text-[11px] text-muted-foreground pt-1">
                  <span className="flex items-center gap-1">
                    <Shield className="w-3 h-3" /> No charge today
                  </span>
                  <span>Cancel anytime</span>
                </div>
              )}
            </form>

            <p className="mt-6 text-center text-sm text-muted-foreground">
              Already have an account?{' '}
              <Link to="/login" className="text-primary font-semibold hover:underline" data-testid="login-link">
                Sign in
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
